import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CognitoConfig, PortalUser, UserRole } from '@models/types';
import { environment } from '@environments/environment';
import { Amplify } from 'aws-amplify';
import {
  signIn, signOut, getCurrentUser, fetchAuthSession, signInWithRedirect, confirmSignIn,
  setUpTOTP, verifyTOTPSetup, resetPassword, confirmResetPassword, updatePassword,
  updateMFAPreference as amplifyUpdateMFAPreference,
  fetchMFAPreference as amplifyFetchMFAPreference,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
  updateUserAttribute,
  fetchUserAttributes
} from 'aws-amplify/auth';
import { LoggerService } from './logger.service';
import { ApiService } from './api.service';
import { firstValueFrom } from 'rxjs';
import { TelemetryService } from './telemetry.service';
import { STORAGE_KEYS } from '../constants/storage-keys';

export type AuthStep = 'CREDENTIALS' | 'PORTAL_SELECTION' | 'NEW_PASSWORD' | 'MFA_METHOD_SELECTION' | 'MFA_SETUP' | 'MFA_VERIFY' | 'DONE' | 'FORGOT_PASSWORD_REQUEST' | 'FORGOT_PASSWORD_CONFIRM';
export type MFAMethod = 'TOTP' | 'SMS' | 'EMAIL';

export interface MFAPreference {
  enabled?: MFAMethod[];
  preferred?: MFAMethod;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private router = inject(Router);
  private logger = inject(LoggerService);
  private apiService = inject(ApiService);
  private telemetry = inject(TelemetryService);

  private _accessToken = signal<string | null>(null);
  isAuthenticated = computed(() => !!this._accessToken());
  currentUser = signal<{ username: string; userId: string; email?: string | null } | null>(null);

  // Configuration State
  private config = signal<CognitoConfig | null>(null);

  /**
   * Reactive signal for the logged-in user's stored details (from /me API).
   * Components should prefer this over getStoredUserDetails() so that the UI
   * updates automatically when the data is fetched or changed.
   */
  readonly storedUser = signal<PortalUser | null>(null);

  // Token refresh management
  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes (tokens expire in 1 hour)
  private isUserActive = true; // Track user activity

  constructor() {
    this.loadConfig();
    this.initializeAmplify();
    this.checkSession();
  }

  // Exposed for Interceptors
  getAccessToken(): string | null {
    return this._accessToken();
  }

  private loadConfig() {
    // Priority: LocalStorage (Runtime override) -> Environment (Build time)
    const stored = localStorage.getItem(STORAGE_KEYS.COGNITO_CONFIG);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (this.isValidCognitoConfig(parsed)) {
          this.config.set(parsed);
          return;
        }
      } catch (error) {
        this.logger.warn('Invalid Cognito config in localStorage, falling back to environment config', error);
      }
      localStorage.removeItem(STORAGE_KEYS.COGNITO_CONFIG);
    }

    const envCognito = environment?.cognito;
    if (this.isValidCognitoConfig(envCognito)) {
      this.config.set(envCognito);
    }
  }

  private isValidCognitoConfig(config: unknown): config is CognitoConfig {
    if (!config || typeof config !== 'object') {
      return false;
    }

    const candidate = config as Partial<CognitoConfig>;
    return (
      typeof candidate.region === 'string' &&
      candidate.region.length > 0 &&
      typeof candidate.userPoolId === 'string' &&
      candidate.userPoolId.length > 0 &&
      typeof candidate.userPoolClientId === 'string' &&
      candidate.userPoolClientId.length > 0
    );
  }

  saveConfig(config: CognitoConfig) {
    localStorage.setItem(STORAGE_KEYS.COGNITO_CONFIG, JSON.stringify(config));
    this.config.set(config);
    this.initializeAmplify();
    window.location.reload();
  }

  getConfig() {
    return this.config();
  }

  private initializeAmplify() {
    const c = this.config();
    if (c && c.userPoolId) {
      try {
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: c.userPoolId,
              userPoolClientId: c.userPoolClientId
            }
          }
        });
      } catch (e) {
        this.logger.error('Failed to configure Amplify', e);
      }
    }
  }

  private async checkSession() {
    if (!this.config()) return;

    try {
      const { tokens } = await fetchAuthSession();
      const user = await getCurrentUser();

      // Decode ID token to extract standard claims like email and sub (user id)
      let email: string | null = null;
      let sub: string | null = null;
      const idToken = tokens?.idToken?.toString();

      if (idToken) {
        try {
          const payload = JSON.parse(atob(idToken.split('.')[1] || '')) as {
            email?: string;
            sub?: string;
          };
          email = payload.email ?? null;
          sub = payload.sub ?? null;
        } catch (e) {
          this.logger.error('Failed to decode Cognito ID token', e);
        }
      }

      if (tokens?.accessToken && user) {
        this._accessToken.set(tokens.accessToken.toString());
        this.currentUser.set({
          username: user.username,
          // Prefer Cognito subject (sub) as stable user id when available
          userId: sub || user.userId,
          email,
        });

        // Ensure user details are loaded from localStorage or fetched if missing
        if (!this.getStoredUserDetails()) {
          await this.fetchAndStoreUserDetails();
        }

        // Start token refresh when session is active
        this.startTokenRefresh();
      }
    } catch (err) {
      this._accessToken.set(null);
      this.stopTokenRefresh();
    }
  }

  // Returns the next step required
  async login(username: string, password: string): Promise<{ success: boolean; nextStep?: string }> {
    this.clearStoredUserDetails();

    if (!this.config()) {
      throw new Error('Cognito configuration is missing. Please configure authentication.');
    }

    try {
      const result = await signIn({ username, password });
      return this.handleSignInResult(result);
    } catch (error) {
      if (this.isUserAlreadyAuthenticatedError(error)) {
        // A stale Cognito session is still active (e.g. the user navigated back to
        // /login while already signed in). Clear it and retry once.
        try {
          await signOut();
          const result = await signIn({ username, password });
          return this.handleSignInResult(result);
        } catch (retryError) {
          this.logger.error('Login failed after clearing stale session', retryError);
          throw retryError;
        }
      }
      this.logger.error('Login failed', error);
      throw error;
    }
  }

  private isUserAlreadyAuthenticatedError(error: unknown): boolean {
    return (error as { name?: string })?.name === 'UserAlreadyAuthenticatedException';
  }


  async confirmNewPassword(newPassword: string): Promise<{ success: boolean; nextStep?: string }> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      return this.handleSignInResult(result);
    } catch (error) {
      this.logger.error('Confirm Password failed', error);
      throw error;
    }
  }

  async confirmMfa(code: string): Promise<{ success: boolean; nextStep?: string }> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      const result = await confirmSignIn({ challengeResponse: code });
      return this.handleSignInResult(result);
    } catch (error) {
      this.logger.error('Confirm MFA failed', error);
      throw error;
    }
  }

  async setupTotp(): Promise<{ secret: string, uri: string }> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }
    // Returns shared secret to generate QR code
    const totpSetupDetails = await setUpTOTP();
    return {
      secret: totpSetupDetails.sharedSecret.toString(),
      uri: totpSetupDetails.getSetupUri('CurrentWare').toString()
    };
  }

  async verifyTotp(code: string): Promise<{ success: boolean; nextStep?: string }> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      await verifyTOTPSetup({ code });
      return { success: true };
    } catch (error) {
      this.logger.error('Verify TOTP failed', error);
      throw error;
    }
  }

  /**
   * Send SMS verification code to the user's phone number.
   * If a phone number is provided, it first updates the phone_number attribute in Cognito,
   * then sends the verification code. This handles the case where the user has no phone in Cognito.
   * Used from Profile MFA setup.
   */
  async sendSmsVerificationCode(phoneNumber?: string): Promise<void> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      // If a phone number is provided, normalize and update it in Cognito first.
      if (phoneNumber) {
        const formattedPhone = this.normalizeToE164(phoneNumber);

        try {
          await updateUserAttribute({
            userAttribute: { attributeKey: 'phone_number', value: formattedPhone }
          });
          this.logger.log('Phone number updated in Cognito:', formattedPhone);
        } catch (error) {
          // Cognito returns this when the attribute value is unchanged.
          // We should still continue and request a new verification code.
          if (!this.isNoOpPhoneUpdateError(error)) {
            throw error;
          }
          this.logger.debug('Phone number already present in Cognito, continuing to send verification code');
        }

        // Keep local cache normalized so profile and MFA flows stay consistent.
        const user = this.getStoredUserDetails();
        if (user) {
          const updated = { ...user, phone: phoneNumber };
          localStorage.setItem(STORAGE_KEYS.USER_DETAILS, JSON.stringify(updated));
          this.storedUser.set(updated);
        }
      }

      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      this.logger.log('SMS verification code sent');
    } catch (error) {
      this.logger.error('Failed to send SMS verification code', error);
      throw error;
    }
  }

  private normalizeToE164(phoneNumber: string): string {
    const trimmed = phoneNumber.trim();
    const digitsOnly = trimmed.replace(/\D/g, '');

    // Basic E.164 guard (max 15 digits, non-zero country code start)
    if (!digitsOnly || digitsOnly.length > 15 || digitsOnly[0] === '0') {
      throw new Error(`Invalid phone number format: ${phoneNumber}`);
    }

    return `+${digitsOnly}`;
  }

  private isNoOpPhoneUpdateError(error: unknown): boolean {
    const name = (error as { name?: string })?.name ?? '';
    const message = error instanceof Error ? error.message : String(error ?? '');
    const normalized = `${name} ${message}`.toLowerCase();
    return normalized.includes('no updates are to be performed');
  }

  /**
   * Verify the SMS code by confirming the phone_number attribute.
   * This is step 3 of the AWS Amplify v6 SMS-MFA setup sequence:
   *   1. updateUserAttribute  (sendSmsVerificationCode)
   *   2. sendUserAttributeVerificationCode  (sendSmsVerificationCode)
   *   3. confirmUserAttribute  ← this method
   *   4. updateMFAPreference  ← caller's responsibility
   * Keeping attribute confirmation separate from preference update lets the
   * caller decide the preferred MFA state after a successful verification.
   * Used from Profile MFA setup.
   */
  async verifySmsAndEnableMfa(code: string): Promise<void> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      // Confirm the phone_number attribute with the verification code received via SMS.
      await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode: code });
      this.logger.log('Phone number attribute verified successfully');
    } catch (error) {
      this.logger.error('Failed to confirm phone number attribute', error);
      throw error;
    }
  }

  /**
   * Setup SMS MFA during sign-in challenge flow.
   * Used from Login component when Cognito requires SMS MFA setup.
   */
  async setupSMS(): Promise<{ destination: string }> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }
    return { destination: 'Your phone number' };
  }

  /**
   * Verify SMS code during sign-in MFA challenge.
   * Used from Login component when Cognito sends an SMS challenge.
   */
  async verifySMS(code: string): Promise<{ success: boolean; nextStep?: string }> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      const result = await confirmSignIn({ challengeResponse: code });
      return this.handleSignInResult(result);
    } catch (error) {
      this.logger.error('Verify SMS failed', error);
      throw error;
    }
  }

  async setupEmail(): Promise<{ destination: string }> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    // In real implementation, this would trigger Email MFA setup
    // The email is sent automatically by Cognito when the MFA method is selected
    return { destination: 'Your email address' };
  }

  async verifyEmail(code: string): Promise<{ success: boolean; nextStep?: string }> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      const result = await confirmSignIn({ challengeResponse: code });
      return this.handleSignInResult(result);
    } catch (error) {
      this.logger.error('Verify Email failed', error);
      throw error;
    }
  }

  async requestPasswordReset(username: string): Promise<void> {
    this.clearStoredUserDetails();
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }
    await resetPassword({ username });
  }

  async requestPasswordResetByEmail(userEmail: string): Promise<void> {
    this.clearStoredUserDetails();
    await firstValueFrom(this.apiService.resetPasswordByEmail(userEmail));
  }

  async completePasswordReset(username: string, code: string, newPassword: string): Promise<void> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }
    await confirmResetPassword({ username, confirmationCode: code, newPassword });
  }

  private async handleSignInResult(result: { isSignedIn: boolean; nextStep?: { signInStep: string } }): Promise<{ success: boolean; nextStep?: string }> {
    const { isSignedIn, nextStep } = result;
    if (isSignedIn) {
      await this.checkSession();
      // Ensure user details are fetched if checkSession didn't (redundancy check)
      if (!this.getStoredUserDetails()) {
        await this.fetchAndStoreUserDetails();
      }
      return { success: true };
    } else {
      this.logger.debug('Next Step Required:', nextStep?.signInStep);
      return { success: false, nextStep: nextStep?.signInStep };
    }
  }

  /**
   * Fetch logged-in user details from API and store in localStorage
   */
  async fetchAndStoreUserDetails(): Promise<PortalUser | null> {
    try {
      this.logger.log('Fetching logged-in user details...');

      const response = await firstValueFrom(this.apiService.getCurrentUser());
      const userDetails: PortalUser = response.data || response;
      if (userDetails) {        // Merge phone_number from Cognito attributes if the backend doesn't return it
        if (!userDetails.phone) {
          try {
            const attrs = await fetchUserAttributes();
            if (attrs.phone_number) userDetails.phone = attrs.phone_number;
          } catch { /* non-fatal — phone stays empty */ }
        }

        // Store user details in localStorage
        localStorage.setItem(STORAGE_KEYS.USER_DETAILS, JSON.stringify(userDetails));
        this.storedUser.set(userDetails);
        this.logger.log('User details stored in localStorage:', userDetails);
        return userDetails;
      }
      return null;
    } catch (error) {
      this.logger.error('Failed to fetch user details:', error);
      // In production, log out when backend is unreachable
      // In dev, keep the session alive so the token stays attached for debugging
      if (environment?.production) {
        await this.logout();
      }
      return null;
    }
  }

  /**
   * Get stored user details from localStorage
   */
  getStoredUserDetails(): PortalUser | null {
    if (this.storedUser()) {
      return this.storedUser();
    }
    const stored = localStorage.getItem(STORAGE_KEYS.USER_DETAILS);
    if (stored) {
      try {
        const user: PortalUser = JSON.parse(stored);
        this.storedUser.set(user);
        return user;
      } catch (e) {
        this.logger.error('Failed to parse stored user details', e);
        return null;
      }
    }
    return null;
  }

  /**
   * Clear stored user details from localStorage
   */
  clearStoredUserDetails(): void {
    localStorage.removeItem(STORAGE_KEYS.USER_DETAILS);
    this.storedUser.set(null);
  }

  /**
   * Persist user details to localStorage and keep the in-memory cache in sync.
   */
  setStoredUserDetails(user: PortalUser): void {
    localStorage.setItem(STORAGE_KEYS.USER_DETAILS, JSON.stringify(user));
    this.storedUser.set(user);
  }

  /**
   * Check if current user has a specific role
   */
  hasRole(role: UserRole): boolean {
    const user = this.getStoredUserDetails();
    return user?.role === role;
  }

  /**
   * Check if current user has any of the specified roles
   */
  hasAnyRole(roles: UserRole[]): boolean {
    const user = this.getStoredUserDetails();
    return user ? roles.includes(user.role) : false;
  }

  /**
   * Check if current user can perform a specific action based on role
   * @param action - The action to check permission for
   * @returns boolean indicating if user has permission
   */
  canPerformAction(action: 'create' | 'edit' | 'delete' | 'view' | 'manage_users' | 'manage_customers' | 'broadcast'): boolean {
    const user = this.getStoredUserDetails();
    if (!user) return false;

    // Define role-based permissions
    const permissions: Record<UserRole, string[]> = {
      'Admin': ['create', 'edit', 'delete', 'view', 'manage_users', 'manage_customers', 'broadcast'],
      'Support': ['create', 'edit', 'view', 'manage_customers'],
      'Viewer': ['view'],
      'MspManager': ['view', 'edit']
    };

    return permissions[user.role]?.includes(action) || false;
  }

  /**
   * Check if current user is Admin
   */
  isAdmin(): boolean {
    return this.hasRole('Admin');
  }

  /**
   * Check if current user is Support
   */
  isSupport(): boolean {
    return this.hasRole('Support');
  }

  /**
   * Check if current user is Viewer
   */
  isViewer(): boolean {
    return this.hasRole('Viewer');
  }

  /** 
   * Check if current user is MSP Manager
   */
  isMspManager(): boolean {
    return this.hasRole('MspManager');
  }
  /**
   * Get current user's role
   */
  getCurrentUserRole(): UserRole | null {
    const user = this.getStoredUserDetails();
    return user?.role || null;
  }

  async loginWithGoogle() {
    this.clearStoredUserDetails();
    if (!this.config()) {
      alert('Please configure AWS Cognito settings first.');
      return;
    }
    this.telemetry.trackEvent('sso_login_attempt', { provider: 'Google' });
    await signInWithRedirect({ provider: 'Google' });
  }

  /**
   * Update MFA preference for the current user.
   * When TOTP is not explicitly included in the `enabled` list, the existing
   * Cognito TOTP state is fetched and preserved so that enabling SMS as
   * PREFERRED never accidentally disables a previously-active TOTP factor.
   */
  async updateMFAPreference(preference: MFAPreference): Promise<void> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      const { enabled, preferred } = preference;

      // Resolve the TOTP disposition for the Amplify call.
      // If enabled is explicitly provided (even as []), treat it as authoritative.
      // Only preserve existing TOTP state when enabled is undefined (caller didn't specify).
      let totpDisposition: 'PREFERRED' | 'ENABLED' | 'DISABLED';
      if (enabled !== undefined) {
        // Caller explicitly declared the full enabled list — respect it
        if (enabled.includes('TOTP')) {
          totpDisposition = preferred === 'TOTP' ? 'PREFERRED' : 'ENABLED';
        } else {
          totpDisposition = 'DISABLED';
        }
      } else {
        // Caller didn't specify — preserve existing TOTP state to avoid accidental disable
        try {
          const current = await amplifyFetchMFAPreference();
          if (current.preferred === 'TOTP' || current.enabled?.includes('TOTP')) {
            totpDisposition = 'ENABLED';
          } else {
            totpDisposition = 'DISABLED';
          }
        } catch {
          totpDisposition = 'DISABLED';
        }
      }

      await amplifyUpdateMFAPreference({
        totp: totpDisposition,
        sms: enabled?.includes('SMS') ? (preferred === 'SMS' ? 'PREFERRED' : 'ENABLED') : 'DISABLED'
      });

      this.telemetry.trackEvent('mfa_preference_updated', { enabled, preferred });
      this.logger.log('MFA preference updated successfully');

      // Keep local user details (and admin views) in sync with MFA status
      const anyEnabled = !!enabled && enabled.length > 0;
      const user = this.getStoredUserDetails();
      if (user) {
        const updated = { ...user, mfaEnabled: anyEnabled };
        localStorage.setItem(STORAGE_KEYS.USER_DETAILS, JSON.stringify(updated));
        this.storedUser.set(updated);

        // Best-effort update to backend user record so admin MFA status reflects reality
        const userId = (updated as PortalUser).userId || updated.id;
        if (userId) {
          try {
            await firstValueFrom(this.apiService.updateUserMfaStatus(userId, anyEnabled));
          } catch (e) {
            this.logger.warn('Failed to update user MFA flag in backend', e);
          }
        }
      }
    } catch (error) {
      this.logger.error('Failed to update MFA preference', error);
      throw error;
    }
  }

  /**
   * Fetch current MFA preference for the user
   */
  async fetchMFAPreference(): Promise<MFAPreference> {
    if (!this.config()) {
      throw new Error('Cognito configuration is missing.');
    }

    try {
      const mfaPreference: { enabled?: MFAMethod[]; preferred?: MFAMethod } = await amplifyFetchMFAPreference();

      const enabled: MFAMethod[] = mfaPreference.enabled || [];
      const preferred: MFAMethod | undefined = mfaPreference.preferred;

      this.logger.log('MFA preference fetched', { enabled, preferred });
      return { enabled, preferred };
    } catch (error) {
      this.logger.error('Failed to fetch MFA preference', error);
      throw error;
    }
  }

  /**
   * Set user activity status (called by IdleService)
   */
  setUserActivity(isActive: boolean): void {
    this.isUserActive = isActive;
    this.logger.debug(`User activity status: ${isActive ? 'active' : 'idle'}`);
  }

  /**
   * Start automatic token refresh
   */
  private startTokenRefresh(): void {
    // Clear any existing refresh interval
    this.stopTokenRefresh();

    // Set up periodic token refresh
    this.refreshIntervalId = setInterval(async () => {
      if (this.isUserActive && this.isAuthenticated()) {
        await this.refreshToken();
      } else {
        this.logger.debug('Skipping token refresh - user is idle or not authenticated');
      }
    }, this.TOKEN_REFRESH_INTERVAL);

    this.logger.log('Token refresh started');
  }

  /**
   * Stop automatic token refresh
   */
  private stopTokenRefresh(): void {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
      this.logger.log('Token refresh stopped');
    }
  }

  /**
   * Manually refresh the access token
   */
  async refreshToken(): Promise<void> {
    if (!this.config()) {
      this.logger.warn('Cannot refresh token: Cognito configuration is missing');
      return;
    }

    try {
      this.logger.debug('Refreshing access token...');

      const { tokens } = await fetchAuthSession({ forceRefresh: true });

      if (tokens?.accessToken) {
        this._accessToken.set(tokens.accessToken.toString());
        this.logger.log('Token refreshed successfully');
      }
    } catch (error) {
      this.logger.error('Token refresh failed', error);
      this.telemetry.trackEvent('token_refresh_failed', {});
      // If refresh fails, user session is likely expired
      await this.logout();
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    this.logger.debug('Attempting to change user password');
    try {
      await updatePassword({ oldPassword, newPassword });
      this.logger.log('Password changed successfully');
      this.telemetry.trackEvent('password_change_success', { username: this.currentUser()?.username });
    } catch (error) {
      this.logger.error('Failed to change password', error);
      this.telemetry.trackEvent('password_change_failed', { username: this.currentUser()?.username, error: (error as Error).message });
      throw error;
    }
  }

  getLastLoginTime(): string | null {
    const token = this._accessToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const authTime = payload['auth_time'];
      return authTime ? new Date(authTime * 1000).toISOString() : null;
    } catch {
      return null;
    }
  }

  /**
   * Convenience helpers to access current Cognito user details
   */
  getCurrentUserEmail(): string | null {
    return this.currentUser()?.email ?? null;
  }

  getCurrentUserId(): string | null {
    return this.currentUser()?.userId ?? null;
  }

  getCurrentUsername(): string | null {
    return this.currentUser()?.username ?? null;
  }

  async logout() {
    // Stop token refresh
    this.stopTokenRefresh();
    this.telemetry.trackEvent('logout', { username: this.currentUser()?.username });
    try {
      if (this.config()) {
        await signOut();
      }
    } catch (e) {
      this.logger.error('Sign out error', e);
    } finally {
      this._accessToken.set(null);
      this.currentUser.set(null);
      // Clear stored user details
      this.clearStoredUserDetails();
      this.router.navigate(['/login']);
    }
  }
}
